import type { Task } from './models'

/*
 * **Unteraufgaben als Baum** (#181): Eine Unteraufgabe darf eigene Unteraufgaben tragen, bis zu
 * vier Ebenen unter der Aufgabe (wie Todoist). Reine Rechnung auf den Zeilen, die eine Quelle
 * hereinreicht — Dexie holt dafür die Kette oder den Ast, die Speicher-Quelle hat alles.
 *
 * Kaputte Daten (ein Zyklus aus Sync oder einer älteren Version) halten an, statt endlos zu laufen.
 */

/** Ebenen unter der Aufgabe: Ebene 1 hängt direkt an ihr, Ebene 4 trägt keine Checkliste mehr. */
export const MAX_SUBTASK_DEPTH = 4

type TreeRow = Pick<Task, 'id' | 'parentTaskId' | 'order' | 'deletedAt'>

/** Die Geschwister, die zählen — z. B. ohne erledigte Runden (`withoutSucceededRounds`). */
export type SiblingFilter<T extends TreeRow> = (siblings: readonly T[]) => readonly T[]

/**
 * Die Kette über `id`, vom Eltern-Teil hinauf bis zur Aufgabe. Gelöschte Glieder zählen mit:
 * Wer fragt, ob eine Unteraufgabe noch irgendwo steht, muss sie sehen.
 */
export function ancestorsOf<T extends TreeRow>(tasks: readonly T[], id: string): T[] {
  const byId = new Map(tasks.map(t => [t.id, t]))
  const chain: T[] = []
  const seen = new Set([id])
  let parentId = byId.get(id)?.parentTaskId
  while (parentId != null && !seen.has(parentId)) {
    const parent = byId.get(parentId)
    if (!parent) break
    seen.add(parentId)
    chain.push(parent)
    parentId = parent.parentTaskId
  }
  return chain
}

/** 0 für eine Aufgabe, 1 für ihre Unteraufgaben, … bis {@link MAX_SUBTASK_DEPTH}. */
export function depthOf(tasks: readonly TreeRow[], id: string): number {
  return ancestorsOf(tasks, id).length
}

/** Die Aufgabe ganz oben; für eine Aufgabe sie selbst, `undefined` ohne Zeile. */
export function rootOf<T extends TreeRow>(tasks: readonly T[], id: string): T | undefined {
  const chain = ancestorsOf(tasks, id)
  return chain[chain.length - 1] ?? tasks.find(t => t.id === id)
}

/**
 * Die lebenden Kinder jeder Zeile, nach `order`. Wer viele Bäume aus denselben Zeilen rechnet (eine Liste mit
 * tausend Aufgaben), baut sie einmal und reicht sie an `descendantsOf`/`treeEntriesOf` weiter — sonst entstünde
 * sie je Aufgabe neu (gemessen 14.10.2026: 80 % der Zeit beim Umschalten auf Demnächst am Handy).
 */
export function childMap<T extends TreeRow>(tasks: readonly T[]): Map<string, T[]> {
  const children = new Map<string, T[]>()
  for (const task of tasks) {
    if (task.deletedAt || task.parentTaskId == null) continue
    const list = children.get(task.parentTaskId)
    if (list) list.push(task)
    else children.set(task.parentTaskId, [task])
  }
  for (const list of children.values()) list.sort((a, b) => a.order - b.order)
  return children
}

/**
 * Alle lebenden Nachkommen in **Vorordnung** — eine Zeile, dann ihr Ast, dann das nächste
 * Geschwister: die Reihenfolge der Checkliste. Was `keep` aus einer Geschwistergruppe nimmt,
 * fehlt samt Ast; gelöschte Zeilen ebenso.
 */
export function descendantsOf<T extends TreeRow>(
  tasks: readonly T[],
  id: string,
  keep?: SiblingFilter<T>,
  children: ReadonlyMap<string, T[]> = childMap(tasks),
): T[] {
  const out: T[] = []
  const seen = new Set([id])
  const walk = (parentId: string) => {
    const siblings = children.get(parentId) ?? []
    for (const child of keep ? keep(siblings) : siblings) {
      if (seen.has(child.id)) continue
      seen.add(child.id)
      out.push(child)
      walk(child.id)
    }
  }
  walk(id)
  return out
}

/** Eine Zeile der Checkliste als Baum: Ebene unter `id` (1 = direkt darunter), Stand der direkten Kinder. */
export interface TreeEntry<T> {
  task: T
  depth: number
  progress: { done: number; total: number }
}

/**
 * Die Checkliste unter `id` als Baum, in Vorordnung (#181). Der Fortschritt zählt nur die direkten
 * Kinder, die `keep` stehen lässt (Grill: auf jeder Ebene gleich).
 */
export function treeEntriesOf<T extends TreeRow & Pick<Task, 'status'>>(
  tasks: readonly T[],
  id: string,
  keep?: SiblingFilter<T>,
  children: ReadonlyMap<string, T[]> = childMap(tasks),
): TreeEntry<T>[] {
  const kept = (parentId: string) => {
    const list = children.get(parentId) ?? []
    return keep ? keep(list) : list
  }
  const out: TreeEntry<T>[] = []
  const seen = new Set([id])
  const walk = (parentId: string, depth: number) => {
    for (const child of kept(parentId)) {
      if (seen.has(child.id)) continue
      seen.add(child.id)
      const own = kept(child.id)
      out.push({ task: child, depth, progress: { done: own.filter(c => c.status === 'completed').length, total: own.length } })
      walk(child.id, depth + 1)
    }
  }
  walk(id, 1)
  return out
}

/** Wie viele Ebenen unter `id` hängen: 0 ohne Kinder. Ein Ast der Höhe 2 passt nur unter Ebene ≤ 2. */
export function subtreeHeight(tasks: readonly TreeRow[], id: string): number {
  const children = childMap(tasks)
  const seen = new Set([id])
  const height = (parentId: string): number => {
    let deepest = 0
    for (const child of children.get(parentId) ?? []) {
      if (seen.has(child.id)) continue
      seen.add(child.id)
      deepest = Math.max(deepest, 1 + height(child.id))
    }
    return deepest
  }
  return height(id)
}

/** Jede Zeile einmal — Dexie holt Kette, Ast und Geschwister einzeln, die überschneiden sich. */
export function uniqueById<T extends { id: string }>(rows: readonly T[]): T[] {
  return [...new Map(rows.map(row => [row.id, row])).values()]
}
